(() => {
  'use strict';
  if (window.vorugPdpDiscoveryR14) return;
  window.vorugPdpDiscoveryR14 = true;
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const railBindings = new WeakMap();
  const sectionLifecycles = new WeakMap();
  const feedBindings = new WeakMap();
  const recommendationHasFocus = () => Boolean(document.activeElement?.closest?.('[data-discovery-rail], [data-discovery-scrollbar]'));

  function reconcileRecommendations() {
    document.querySelectorAll('.vpd-related[data-vorug-discovery]').forEach(section => {
      const rail = section.querySelector('[data-discovery-rail]');
      if (!rail) return;
      Array.from(rail.children).slice(20).forEach(node => node.remove());
      section.hidden = !rail.children.length;
      section.dataset.empty = String(!rail.children.length);
      bindRail(section);
    });
  }

  function bindScrollbar(section, rail, abort) {
    if (!section.matches('.vpd-related')) return () => {};
    let scrollbar = section.querySelector('[data-discovery-scrollbar]');
    if (!scrollbar) {
      scrollbar = document.createElement('div');
      scrollbar.className = 'vpd-rail-scrollbar';
      scrollbar.dataset.discoveryScrollbar = '';
      scrollbar.setAttribute('role', 'scrollbar');
      scrollbar.setAttribute('aria-orientation', 'horizontal');
      scrollbar.setAttribute('aria-valuemin', '0');
      scrollbar.setAttribute('aria-valuemax', '100');
      scrollbar.setAttribute('aria-label', `${section.querySelector('.vpd-header h2')?.textContent.trim() || 'Keep exploring'} scroll`);
      const thumb = document.createElement('span');
      thumb.className = 'vpd-rail-scrollbar__thumb';
      thumb.setAttribute('aria-hidden', 'true');
      scrollbar.append(thumb);
    }
    rail.after(scrollbar);
    rail.id ||= `VorugDiscoveryRail-${section.dataset.sectionId || section.parentElement.id}`;
    scrollbar.setAttribute('aria-controls', rail.id);
    section.dataset.discoveryScrollbarReady = 'true';
    const thumb = scrollbar.firstElementChild;
    const options = { signal: abort.signal };
    const max = () => Math.max(0, rail.scrollWidth - rail.clientWidth);
    const rtl = () => getComputedStyle(rail).direction === 'rtl';
    const current = () => Math.abs(rail.scrollLeft);
    const move = value => rail.scrollTo({ left: (rtl() ? -1 : 1) * Math.max(0, Math.min(max(), value)), behavior: 'auto' });
    const update = () => {
      const available = max();
      const width = scrollbar.clientWidth;
      const thumbWidth = Math.min(width, Math.max(44, width * rail.clientWidth / Math.max(1, rail.scrollWidth)));
      const progress = available ? Math.min(1, current() / available) : 0;
      scrollbar.style.setProperty('--vpd-scrollbar-width', `${thumbWidth}px`);
      scrollbar.style.setProperty('--vpd-scrollbar-left', `${(rtl() ? 1 - progress : progress) * (width - thumbWidth)}px`);
      scrollbar.setAttribute('aria-valuenow', String(Math.round(progress * 100)));
      scrollbar.setAttribute('aria-disabled', String(!available));
      scrollbar.tabIndex = available ? 0 : -1;
    };
    let pointer = null, offset = 0, snap = '';
    const drag = event => {
      const bounds = scrollbar.getBoundingClientRect();
      const distance = bounds.width - thumb.getBoundingClientRect().width;
      const fraction = distance > 0 ? Math.max(0, Math.min(1, (event.clientX - bounds.left - offset) / distance)) : 0;
      move((rtl() ? 1 - fraction : fraction) * max());
      update();
    };
    const stop = () => {
      if (pointer === null) return;
      pointer = null;
      rail.style.scrollSnapType = snap;
      update();
    };
    scrollbar.addEventListener('pointerdown', event => {
      if (!max() || event.button !== 0) return;
      event.preventDefault();
      scrollbar.focus({ preventScroll: true });
      pointer = event.pointerId;
      offset = event.target === thumb ? event.clientX - thumb.getBoundingClientRect().left : thumb.getBoundingClientRect().width / 2;
      snap = rail.style.scrollSnapType;
      rail.style.scrollSnapType = 'none';
      scrollbar.setPointerCapture(pointer);
      drag(event);
    }, options);
    scrollbar.addEventListener('pointermove', event => { if (pointer === event.pointerId) drag(event); }, options);
    scrollbar.addEventListener('pointerup', stop, options);
    scrollbar.addEventListener('pointercancel', stop, options);
    abort.signal.addEventListener('abort', stop, { once: true });
    scrollbar.addEventListener('keydown', event => {
      const direction = rtl() ? -1 : 1;
      const targets = { ArrowLeft: current() - 40 * direction, ArrowRight: current() + 40 * direction, PageUp: current() - rail.clientWidth, PageDown: current() + rail.clientWidth, Home: 0, End: max(), ' ': current() + rail.clientWidth * (event.shiftKey ? -1 : 1) };
      if (!(event.key in targets) || !max()) return;
      event.preventDefault();
      move(targets[event.key]);
      update();
    }, options);
    return update;
  }

  function bindRail(section) {
    const old = railBindings.get(section);
    const rail = section.querySelector('[data-discovery-rail]');
    const previous = section.querySelector('[data-discovery-previous]');
    const next = section.querySelector('[data-discovery-next]');
    const scrollbar = section.querySelector('[data-discovery-scrollbar]');
    if (old && old.rail === rail && old.previous === previous && old.next === next &&
        old.scrollbar === scrollbar && old.thumb === scrollbar?.firstElementChild) {
      old.update();
      return;
    }
    old?.abort.abort();
    old?.resize?.disconnect();
    railBindings.delete(section);
    if (!rail) {
      section.querySelector('.vpd-rail-controls')?.setAttribute('hidden', '');
      return;
    }
    section.querySelector('.vpd-rail-controls')?.removeAttribute('hidden');
    const abort = new AbortController();
    const updateScrollbar = bindScrollbar(section, rail, abort);
    const update = () => {
      updateScrollbar();
      const position = Math.abs(rail.scrollLeft);
      if (previous) previous.disabled = position <= 2;
      if (next) next.disabled = position + rail.clientWidth >= rail.scrollWidth - 2;
    };
    const direction = () => getComputedStyle(rail).direction === 'rtl' ? -1 : 1;
    previous?.addEventListener('click', () => rail.scrollBy({ left: -rail.clientWidth * direction(), behavior: reduced() ? 'auto' : 'smooth' }), { signal: abort.signal });
    next?.addEventListener('click', () => rail.scrollBy({ left: rail.clientWidth * direction(), behavior: reduced() ? 'auto' : 'smooth' }), { signal: abort.signal });
    rail.addEventListener('scroll', update, { passive: true, signal: abort.signal });
    const resize = 'ResizeObserver' in window ? new ResizeObserver(update) : null;
    resize?.observe(rail);
    const boundScrollbar = section.querySelector('[data-discovery-scrollbar]');
    railBindings.set(section, { rail, previous, next, scrollbar: boundScrollbar,
      thumb: boundScrollbar?.firstElementChild, abort, resize, update });
    update();
  }

  async function loadRecommendations(section) {
    if (!section.isConnected || section.dataset.recommendationsLoaded) return;
    const lifecycle = sectionLifecycles.get(section);
    if (!lifecycle) return;
    const active = () => section.isConnected && !lifecycle.abort.signal.aborted && sectionLifecycles.get(section) === lifecycle;
    section.dataset.recommendationsLoaded = 'true';
    try {
      const url = new URL(section.dataset.recommendationsUrl, location.origin);
      if (url.origin !== location.origin) return;
      const response = await fetch(url.href, { credentials: 'same-origin', headers: { Accept: 'text/html' }, signal: lifecycle.abort.signal });
      if (!active() || !response.ok) return;
      const text = await response.text();
      if (!active()) return;
      const documentFragment = new DOMParser().parseFromString(text, 'text/html');
      const incoming = Array.from(documentFragment.querySelectorAll('[data-vorug-discovery]')).find(node => node.dataset.sectionId === section.dataset.sectionId && node.dataset.source === 'recommendations');
      const rail = incoming?.querySelector('[data-discovery-rail]');
      if (!rail?.querySelector('[data-positive-price="true"]')) return;
      // Native responses enhance a complete server-rendered fallback; preserve a keyboard
      // interaction in either row, including a last-row item that first-row dedupe could remove.
      if (recommendationHasFocus()) return;
      const existingRail = section.querySelector('[data-discovery-rail]');
      // Keep contextual catalogue matches behind native suggestions, with a hard
      // 20-card ceiling; neither row falls back to a fixed featured-product set.
      const uniqueIds = new Set();
      const merged = [...rail.children, ...(existingRail?.children || [])].filter(node => {
        const id = node.dataset.discoveryProduct;
        if (!id || uniqueIds.has(id)) return false;
        uniqueIds.add(id);
        return true;
      });
      // Stable sorting keeps native order within each relevance tier. Contextual
      // matches lead; unrelated native suggestions fill only unused slots.
      merged.sort((a, b) => Number(b.dataset.relevance || 0) - Number(a.dataset.relevance || 0));
      rail.replaceChildren(...merged.slice(0, 20));
      existingRail ? existingRail.replaceWith(rail) : section.append(rail);
      section.dataset.source = 'recommendations';
      section.dataset.empty = 'false';
      section.hidden = false;
      bindRail(section);
      reconcileRecommendations();
    } catch (_) {
      // The verified server-rendered collection remains usable on network/API failure.
    }
  }

  function bindFeaturedFeed(feed) {
    if (feed.dataset.pdpFeedBound) return;
    feed.dataset.pdpFeedBound = 'true';
    const abort = new AbortController();
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const syncMotion = () => {
      if (!feed.swiper) return;
      feed.sliderSpeed = motion.matches ? 0 : 200;
      feed.swiper.params.speed = feed.sliderSpeed;
    };
    // Native homepage Swiper initializes only when visible; one class observer bridges
    // initialization and breakpoint reinitialization without polling or an animation loop.
    const observer = new MutationObserver(() => {
      if (!feed.swiper) return;
      syncMotion();
    });
    observer.observe(feed, { attributes: true, attributeFilter: ['class'] });
    feedBindings.set(feed, { abort, observer });
    syncMotion();
    motion.addEventListener('change', syncMotion, { signal: abort.signal });
    feed.addEventListener('keydown', event => {
      if (event.target !== feed || !feed.swiper) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        syncMotion();
        event.key === 'ArrowLeft' ? feed.swiper.slidePrev() : feed.swiper.slideNext();
      }
    }, { signal: abort.signal });
  }

  function reconcileTradeNotices(root = document) {
    root.querySelectorAll('[data-pdp-trade-form]').forEach(wrapper => {
      const notices = wrapper.querySelectorAll('[data-enquiry-success], [data-enquiry-errors]');
      if (!notices.length) return;
      let hash = window.location.hash.slice(1);
      try { hash = decodeURIComponent(hash); } catch (_) { /* Preserve malformed literal hash. */ }
      const ownResponse = hash === wrapper.dataset.pdpTradeForm;
      notices.forEach(notice => { notice.hidden = !ownResponse; });
      const disclosure = wrapper.querySelector('.vpd-trade-disclosure');
      if (disclosure) disclosure.open = ownResponse;
    });
  }

  function initialize(root = document) {
    root.querySelectorAll('[data-vorug-callback]').forEach(form => {
      if (form.dataset.callbackBound) return;
      form.dataset.callbackBound = 'true';
      form.addEventListener('submit', async event => {
        if (event.defaultPrevented) return;
        event.preventDefault();
        const phone = form.querySelector('[data-enquiry-phone]');
        await phone.vorugPhoneReady;
        if (phone.vorugValidatePhone && !phone.vorugValidatePhone()) {
          phone.reportValidity();
          return;
        }
        const number = phone.vorugGetPhoneNumber?.() || phone.value.trim();
        const target = new URL(form.action, location.origin);
        target.searchParams.set('request', 'callback');
        target.searchParams.set('product', form.dataset.productHandle);
        try {
          sessionStorage.setItem('vorug-callback-phone', number);
        } catch {
          if (!form.dataset.callbackStorageNotice) {
            form.dataset.callbackStorageNotice = 'true';
            form.querySelector('[data-callback-status]').textContent = 'Your browser could not carry your number to the next step. Copy it, then continue to enter it with your email.';
            return;
          }
        }
        location.assign(target.pathname + target.search);
      });
    });
    root.querySelectorAll('[data-vorug-discovery]').forEach(section => {
      if (section.dataset.discoveryBound) return;
      section.dataset.discoveryBound = 'true';
      const lifecycle = { abort: new AbortController(), observer: null };
      sectionLifecycles.set(section, lifecycle);
      bindRail(section);
      // Custom service CTAs follow their Contact href; no purchase-drawer interception.
      if (!section.dataset.recommendationsUrl) return;
      if (section.dataset.empty === 'true') {
        loadRecommendations(section);
        return;
      }
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(entries => {
          if (lifecycle.abort.signal.aborted || sectionLifecycles.get(section) !== lifecycle) return;
          if (entries.some(entry => entry.isIntersecting)) {
            observer.disconnect();
            lifecycle.observer = null;
            loadRecommendations(section);
          }
        }, { rootMargin: '200px' });
        lifecycle.observer = observer;
        observer.observe(section);
      } else loadRecommendations(section);
    });
    root.querySelectorAll('[data-pdp-featured-feed]').forEach(bindFeaturedFeed);
    reconcileRecommendations();
    reconcileTradeNotices(root);
  }
  function dispose(root) {
    const sections = Array.from(root.querySelectorAll('[data-vorug-discovery]'));
    if (root.matches?.('[data-vorug-discovery]')) sections.unshift(root);
    sections.forEach(section => {
      const lifecycle = sectionLifecycles.get(section);
      if (!lifecycle) return;
      lifecycle.abort.abort();
      lifecycle.observer?.disconnect();
      sectionLifecycles.delete(section);
      const binding = railBindings.get(section);
      binding?.abort.abort();
      binding?.resize?.disconnect();
      railBindings.delete(section);
      delete section.dataset.discoveryBound;
      delete section.dataset.recommendationsLoaded;
    });
    const feeds = Array.from(root.querySelectorAll('[data-pdp-featured-feed]'));
    if (root.matches?.('[data-pdp-featured-feed]')) feeds.unshift(root);
    feeds.forEach(feed => {
      const binding = feedBindings.get(feed);
      if (!binding) return;
      binding.abort.abort();
      binding.observer.disconnect();
      feedBindings.delete(feed);
      delete feed.dataset.pdpFeedBound;
    });
  }
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', () => initialize(), { once: true }) : initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => dispose(event.target));
  window.addEventListener('hashchange', () => reconcileTradeNotices());
})();
